/**
 * ╔═══════════════════════════════════════════════════════════════════════════╗
 * ║                                                                           ║
 * ║                    🗺️ WORLD SHAPE ABSTRACTION 🗺️                         ║
 * ║                                                                           ║
 * ║  Unified interface for different map shapes (circular, rectangular).      ║
 * ║  Used by Test Mode to support various map configurations.                 ║
 * ║                                                                           ║
 * ╚═══════════════════════════════════════════════════════════════════════════╝
 */

import { WorldZone, ZONE_BOUNDARIES } from './constants';

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                           MAP SHAPE ENUMS                                 ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Available map shapes
 */
export enum MapShape {
  CIRCULAR = 'circular',
  RECTANGULAR = 'rectangular',
}

/**
 * Map size presets
 */
export enum MapSize {
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large',
}

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                         SIZE CONFIGURATIONS                               ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Size configurations for circular maps (radius)
 */
export const CIRCULAR_SIZE_CONFIG: Record<MapSize, number> = {
  [MapSize.SMALL]: 2500,
  [MapSize.MEDIUM]: 5000,
  [MapSize.LARGE]: 10000,
};

/**
 * Size configurations for rectangular maps (width x height)
 */
export const RECTANGULAR_SIZE_CONFIG: Record<MapSize, { width: number; height: number }> = {
  [MapSize.SMALL]: { width: 5000, height: 5000 },
  [MapSize.MEDIUM]: { width: 10000, height: 10000 },
  [MapSize.LARGE]: { width: 20000, height: 20000 },
};

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                        WORLD BOUNDS INTERFACE                             ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Unified interface for world boundaries
 * Allows game logic to work with both circular and rectangular maps
 */
export interface WorldBounds {
  /** The shape type of this bounds */
  readonly shape: MapShape;

  /** Check if a point is within bounds */
  isInBounds(x: number, y: number): boolean;

  /** Get distance from point to nearest boundary (negative if outside) */
  getDistanceToBoundary(x: number, y: number): number;

  /** Clamp a position to be within bounds with optional padding */
  clampToBoundary(x: number, y: number, padding?: number): { x: number; y: number };

  /** Get a random position within bounds with optional margin from edge */
  getRandomPosition(margin?: number): { x: number; y: number };

  /** Get the total area of the playable space */
  getArea(): number;

  /** Get the zone (CORE/MID/RIM) for a position */
  getZone(x: number, y: number): WorldZone;

  /** Get the center point of the world */
  getCenter(): { x: number; y: number };

  /** Get the "effective radius" for scaling purposes (used by minimap, etc.) */
  getEffectiveRadius(): number;

  /** Get bounds dimensions for rendering */
  getDimensions(): { width: number; height: number; radius?: number };
}

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                         CIRCULAR BOUNDS                                   ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Circular world bounds implementation
 */
export class CircularBounds implements WorldBounds {
  readonly shape = MapShape.CIRCULAR;
  readonly radius: number;

  constructor(radius: number) {
    this.radius = radius;
  }

  isInBounds(x: number, y: number): boolean {
    return Math.sqrt(x * x + y * y) <= this.radius;
  }

  getDistanceToBoundary(x: number, y: number): number {
    return this.radius - Math.sqrt(x * x + y * y);
  }

  clampToBoundary(x: number, y: number, padding: number = 0): { x: number; y: number } {
    const dist = Math.sqrt(x * x + y * y);
    const maxDist = this.radius - padding;

    if (dist > maxDist && dist > 0) {
      const scale = maxDist / dist;
      return { x: x * scale, y: y * scale };
    }

    return { x, y };
  }

  getRandomPosition(margin: number = 0): { x: number; y: number } {
    const effectiveRadius = this.radius - margin;
    // Use square root for uniform distribution in circular area
    const r = Math.sqrt(Math.random()) * effectiveRadius;
    const theta = Math.random() * Math.PI * 2;
    return {
      x: Math.cos(theta) * r,
      y: Math.sin(theta) * r,
    };
  }

  getArea(): number {
    return Math.PI * this.radius * this.radius;
  }

  getZone(x: number, y: number): WorldZone {
    const distanceFromCenter = Math.sqrt(x * x + y * y);
    const normalizedDistance = distanceFromCenter / this.radius;

    if (normalizedDistance <= ZONE_BOUNDARIES.CORE_END) {
      return WorldZone.CORE;
    } else if (normalizedDistance <= ZONE_BOUNDARIES.MID_END) {
      return WorldZone.MID;
    } else {
      return WorldZone.RIM;
    }
  }

  getCenter(): { x: number; y: number } {
    return { x: 0, y: 0 };
  }

  getEffectiveRadius(): number {
    return this.radius;
  }

  getDimensions(): { width: number; height: number; radius: number } {
    return {
      width: this.radius * 2,
      height: this.radius * 2,
      radius: this.radius,
    };
  }
}

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                       RECTANGULAR BOUNDS                                  ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Rectangular world bounds implementation
 */
export class RectangularBounds implements WorldBounds {
  readonly shape = MapShape.RECTANGULAR;
  readonly width: number;
  readonly height: number;
  readonly halfWidth: number;
  readonly halfHeight: number;

  constructor(width: number, height: number) {
    this.width = width;
    this.height = height;
    this.halfWidth = width / 2;
    this.halfHeight = height / 2;
  }

  isInBounds(x: number, y: number): boolean {
    return Math.abs(x) <= this.halfWidth && Math.abs(y) <= this.halfHeight;
  }

  getDistanceToBoundary(x: number, y: number): number {
    // Distance to each wall
    const distLeft = this.halfWidth + x;
    const distRight = this.halfWidth - x;
    const distTop = this.halfHeight + y;
    const distBottom = this.halfHeight - y;

    // Return minimum distance (negative if outside)
    return Math.min(distLeft, distRight, distTop, distBottom);
  }

  clampToBoundary(x: number, y: number, padding: number = 0): { x: number; y: number } {
    const maxX = this.halfWidth - padding;
    const maxY = this.halfHeight - padding;

    return {
      x: Math.max(-maxX, Math.min(maxX, x)),
      y: Math.max(-maxY, Math.min(maxY, y)),
    };
  }

  getRandomPosition(margin: number = 0): { x: number; y: number } {
    const effectiveHalfWidth = this.halfWidth - margin;
    const effectiveHalfHeight = this.halfHeight - margin;

    return {
      x: (Math.random() * 2 - 1) * effectiveHalfWidth,
      y: (Math.random() * 2 - 1) * effectiveHalfHeight,
    };
  }

  getArea(): number {
    return this.width * this.height;
  }

  getZone(x: number, y: number): WorldZone {
    // For rectangular maps, use distance from center normalized by half-diagonal
    const distanceFromCenter = Math.sqrt(x * x + y * y);
    const maxDistance = Math.sqrt(this.halfWidth * this.halfWidth + this.halfHeight * this.halfHeight);
    const normalizedDistance = distanceFromCenter / maxDistance;

    if (normalizedDistance <= ZONE_BOUNDARIES.CORE_END) {
      return WorldZone.CORE;
    } else if (normalizedDistance <= ZONE_BOUNDARIES.MID_END) {
      return WorldZone.MID;
    } else {
      return WorldZone.RIM;
    }
  }

  getCenter(): { x: number; y: number } {
    return { x: 0, y: 0 };
  }

  getEffectiveRadius(): number {
    // Use half of the smaller dimension for consistent scaling
    return Math.min(this.halfWidth, this.halfHeight);
  }

  getDimensions(): { width: number; height: number } {
    return {
      width: this.width,
      height: this.height,
    };
  }
}

// ╔═══════════════════════════════════════════════════════════════════════════╗
// ║                         FACTORY FUNCTIONS                                 ║
// ╚═══════════════════════════════════════════════════════════════════════════╝

/**
 * Create world bounds from shape and size configuration
 */
export function createWorldBounds(shape: MapShape, size: MapSize): WorldBounds {
  if (shape === MapShape.CIRCULAR) {
    return new CircularBounds(CIRCULAR_SIZE_CONFIG[size]);
  } else {
    const config = RECTANGULAR_SIZE_CONFIG[size];
    return new RectangularBounds(config.width, config.height);
  }
}

/**
 * Create circular bounds with custom radius
 */
export function createCircularBounds(radius: number): CircularBounds {
  return new CircularBounds(radius);
}

/**
 * Create rectangular bounds with custom dimensions
 */
export function createRectangularBounds(width: number, height: number): RectangularBounds {
  return new RectangularBounds(width, height);
}
